/**
 * CarCard.js — Stile premium autonoleggio
 * Card con immagine full-bleed, badge overlay, spec in label uppercase
 */

import React, { useState } from 'react';
import { Car, Eye, EyeOff, Edit2, Trash2, Check, X } from 'lucide-react';
import { getDisplayPrice } from '../utils/helpers';
import { CARD_IMAGE_ASPECT, getCardFraming, framingImageStyle, getCardImageSrc } from '../utils/cardFraming';
import ImageCarousel from './ImageCarousel';
import CarDetailModal from './CarDetailModal';

const MotoIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/>
    <path d="M15 6l-2 4H8.5L7 13.5"/><path d="M15 6h3l1.5 4H15"/>
    <path d="M8.5 10L6 17.5"/><path d="M15 10l1.5 4"/>
  </svg>
);

const CarCard = ({ car, onSelect, showBookButton = false, isAdmin = false, onEdit, onDelete }) => {
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const isMoto = car.tipoVeicolo === 'moto';

  // Sul sito pubblico (non admin), cliccare ovunque sulla card apre la
  // finestra di dettaglio (foto + descrizione + prenotazione). I pulsanti
  // interni (Prenota ora, Foto) fermano la propagazione per non aprire
  // anche il dettaglio quando l'utente vuole un'azione diretta.
  const handleCardClick = () => {
    if (!isAdmin) setShowDetailModal(true);
  };

  return (
    <>
      <div
        className={`${isAdmin ? 'bg-white border border-slate-200/80' : 'bg-white cursor-pointer border border-slate-200/60'} rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group`}
        onClick={handleCardClick}
      >

        {/* ── Immagine ── */}
        {/* Rapporto d'aspetto fisso (uguale all'anteprima di CardFramingEditor):
            l'inquadratura scelta dal noleggiatore si vede identica ovunque. */}
        <div className="relative overflow-hidden bg-slate-100" style={{ aspectRatio: CARD_IMAGE_ASPECT }}>
          {car.immagini && car.immagini.length > 0 ? (
            <div className="w-full h-full group-hover:scale-105 transition-transform duration-500">
              <img
                src={getCardImageSrc(car)}
                alt={`${car.marca} ${car.modello}`}
                loading="lazy"
                className="w-full h-full object-cover"
                style={framingImageStyle(getCardFraming(car))}
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isMoto
                ? <MotoIcon className={isAdmin ? "w-20 h-20 text-slate-200" : "w-14 h-14 text-slate-200"} />
                : <Car className={isAdmin ? "w-20 h-20 text-slate-200" : "w-14 h-14 text-slate-200"} />}
            </div>
          )}

          {/* Badge disponibile / moto */}
          <div className={`absolute flex flex-col gap-1.5 ${isAdmin ? 'top-3 left-3' : 'top-2 left-2'}`}>
            {car.disponibile && !isAdmin && (
              <span className="text-[11px] font-bold uppercase tracking-wider bg-[var(--brand,#2563EB)] text-[var(--brand-on,#fff)] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                {isMoto ? <><MotoIcon className="w-3 h-3" />MOTO</> : <><Check className="w-3 h-3" />DISPONIBILE</>}
              </span>
            )}
            {isMoto && isAdmin && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-white px-2.5 py-0.5 rounded-full inline-flex items-center gap-1">
                <MotoIcon className="w-3 h-3" /> MOTO
              </span>
            )}
          </div>

          {/* Prezzo badge — angolo in alto a destra */}
          <div className={`absolute ${isAdmin ? 'top-3 right-3' : 'top-2 right-2'}`}>
            <div className={`bg-white rounded-xl shadow-sm border border-slate-100 ${isAdmin ? 'px-3 py-1.5' : 'px-2.5 py-1.5'}`}>
              <p className={`text-slate-400 font-medium leading-none mb-0.5 ${isAdmin ? 'text-xs' : 'text-[11px]'}`}>da</p>
              <p className={`text-blue-600 leading-none ${isAdmin ? 'font-black text-base' : 'font-display font-bold text-base'}`}>€{getDisplayPrice(car)}<span className={`font-medium text-slate-400 ${isAdmin ? 'text-xs' : 'text-[11px]'}`}> /g</span></p>
            </div>
          </div>

          {/* Foto count */}
          {car.immagini && car.immagini.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowPhotoModal(true); }}
              className={`absolute bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full font-medium transition flex items-center gap-1 ${isAdmin ? 'bottom-3 right-3 text-xs px-2.5 py-1' : 'bottom-2 right-2 text-[11px] px-2 py-0.5'}`}
            >
              <Eye className="w-3 h-3" />
              {car.immagini.length} foto
            </button>
          )}
        </div>

        {/* ── Corpo ── */}
        <div className={isAdmin ? 'p-5' : 'p-3.5'}>
          {/* Nome */}
          <h3 className={`text-slate-900 tracking-tight mb-1 ${isAdmin ? 'font-black text-lg' : 'font-display font-extrabold text-base'}`}>
            {car.marca} {car.modello}
          </h3>

          {/* Targa */}
          {car.targa && (
            <p className={`font-mono font-bold text-slate-400 tracking-wider ${isAdmin ? 'text-xs mb-3' : 'text-[11px] mb-2'}`}>{car.targa}</p>
          )}

          {/* Specifiche — grid label/valore stile screenshot */}
          <div className={`grid grid-cols-3 gap-x-2 ${isAdmin ? 'gap-y-2 mb-4' : 'gap-y-1 mb-2.5'}`}>
            {car.categoria && (
              <div>
                <p className={`font-bold uppercase tracking-wider text-slate-400 mb-0.5 ${isAdmin ? 'text-xs' : 'text-[11px]'}`}>Classe</p>
                <p className={`font-semibold text-slate-700 capitalize text-xs`}>{car.categoria}</p>
              </div>
            )}
            {car.colore && (
              <div>
                <p className={`font-bold uppercase tracking-wider text-slate-400 mb-0.5 ${isAdmin ? 'text-xs' : 'text-[11px]'}`}>Colore</p>
                <p className={`font-semibold text-slate-700 text-xs`}>{car.colore}</p>
              </div>
            )}
            {car.alimentazione && (
              <div>
                <p className={`font-bold uppercase tracking-wider text-slate-400 mb-0.5 ${isAdmin ? 'text-xs' : 'text-[11px]'}`}>
                  {isMoto ? 'Cilindrata' : 'Motore'}
                </p>
                <p className={`font-semibold text-slate-700 text-xs`}>
                  {isMoto && car.cilindrata ? `${car.cilindrata}cc` : car.alimentazione}
                </p>
              </div>
            )}
          </div>

          {/* Azioni */}
          {isAdmin ? (
            <div className="space-y-2 pt-3 border-t border-slate-100">
              <div className="grid grid-cols-2 gap-2">
                <button onClick={(e) => { e.stopPropagation(); onEdit(car); }}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-bold transition-all">
                  <Edit2 className="w-3.5 h-3.5" /> Modifica
                </button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(car.id); }}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs sm:text-sm font-bold transition">
                  <Trash2 className="w-3.5 h-3.5" /> Elimina
                </button>
              </div>
              {car.immagini && car.immagini.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setShowPhotoModal(true); }}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition">
                  <Eye className="w-3.5 h-3.5" /> Visualizza foto
                </button>
              )}
            </div>
          ) : showBookButton ? (
            <div className="space-y-1.5">
              <button
                onClick={(e) => { e.stopPropagation(); onSelect && onSelect(car); }}
                className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold tracking-wide transition active:scale-95"
              >
                Prenota ora
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setShowDetailModal(true); }}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold transition"
              >
                <Eye className="w-3 h-3" /> Dettagli e foto
              </button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex flex-wrap gap-1.5">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                  car.disponibile ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${car.disponibile ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  {car.disponibile ? 'Disponibile' : 'Non disponibile'}
                </span>
                {car.visibileCatalogo === false && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                    <EyeOff className="w-3 h-3" />
                    Nascosto dal sito
                  </span>
                )}
              </div>
              {car.immagini && car.immagini.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setShowDetailModal(true); }}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold transition">
                  <Eye className="w-3 h-3" /> Dettagli e foto
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal foto (solo pannello admin — sul sito pubblico usa CarDetailModal) */}
      {isAdmin && showPhotoModal && car.immagini && car.immagini.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-3 sm:p-6 z-50" onClick={() => setShowPhotoModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-black text-slate-900">{car.marca} {car.modello}</h2>
                {car.targa && <p className="text-xs font-mono text-slate-400 mt-0.5">{car.targa}</p>}
              </div>
              <button onClick={() => setShowPhotoModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 sm:p-6 flex-1 overflow-hidden">
              <ImageCarousel images={car.immagini} thumbs={car.immaginiThumb} title={`${car.marca} ${car.modello}`} />
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
              <button onClick={() => setShowPhotoModal(false)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finestra di dettaglio (sito pubblico): foto + descrizione + prenota */}
      {!isAdmin && showDetailModal && (
        <CarDetailModal
          car={car}
          showBookButton={showBookButton}
          onClose={() => setShowDetailModal(false)}
          onBook={(c) => { setShowDetailModal(false); onSelect && onSelect(c); }}
        />
      )}
    </>
  );
};

// Audit 2026-09-20, punto #14: React.memo — evita che ogni aggiornamento del
// listener live di bookings/cars (AdminDashboard) ri-renderizzi tutte le card
// a schermo. Con 40 veicoli/200 prenotazioni il lag alla digitazione nei
// campi di ricerca era visibile.
export default React.memo(CarCard);
